import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom, forkJoin, Observable, of } from 'rxjs';
import { catchError, first } from "rxjs/operators";
import { AppServiceHelperService } from './app-service-helper.service';
import { ObjectUtil } from '../components/common/object-util/object-util';

/**
 * Residual Calculation Service
 */
@Injectable()
export class WidgetResidualCalculationService {
  /**
 * Constructor
 * @param httpClient HttpClient
 */
  constructor(private httpClient: HttpClient, private appServiceHelper: AppServiceHelperService) { }
  /**
   * Last report id
   */
  lastReportId: number = null;
  /**
   * Load resource
   */
  loadResource: any = null;
  /**
   * Save resource
   */
  saveResource: any = null;
  /**
   * Load promise
   */
  loadPromise: Promise<any> = null;
  /**
   * Save promise
   */
  savePromise: Promise<any> = null;
  /**
   * Load mockup promise
   */
  loadMockupPromise: Promise<any> = null;

  /**
   * The mockup data observable
   */
  public mockupData$: Observable<any>;

  /**
   * The widget's data object
   */
  private _rvcData: any;

  /**
   * The widget's mockup data object
   */
  protected mockupData: any;

  /**
   * Getter for the residual value calculation data
   */
  protected get rvcData() {
    return this._rvcData;
  }

  /**
   * setter for the residual value calculation data
   * @param data the initial residual value calculation data
   */
  protected set rvcData(data: any) {
    this._rvcData = data;
  }
  /**
   * Reset
   */
  private reset(): void {
    this.loadResource = null;
    this.saveResource = null;
    this.loadPromise = null;
    this.savePromise = null;
    this.loadMockupPromise = null;
    this.rvcData = null;
    this.mockupData = null;
  }
  /**
   * Save
   * @returns
   */
  public save() {
    if (this.savePromise !== null) return this.savePromise;
    else {
      this.savePromise = firstValueFrom(forkJoin([
        this.httpClient.post<any>(this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', 'reportwidgets/residualValueCalculation'), this.rvcData[1]),
        this.httpClient.post<any>(this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', 'reportwidgets/residualValueCalculation'), this.rvcData[2]),
        this.httpClient.post<any>(this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', 'reportwidgets/residualValueCalculation'), this.rvcData[3]),
        this.httpClient.post<any>(this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', 'reportwidgets/residualValueCalculation'), this.rvcData[4])
      ]));
      return this.savePromise;
    }
  }
  /**
   * Load
   * @param reportId
   * @returns
   */
  public load(reportId: number) {
    if (reportId !== this.lastReportId) {
      //Reset if report or template mode is activated/changed!
      this.reset();
      this.lastReportId = reportId;
    }
    if (reportId > 0) {
      return this.loadData();
    } else {
      return this.getMockupData();
    }
  }
  /**
   * Load data
   * @returns
   */
  private loadData() {
    if (this.loadPromise !== null) return this.loadPromise;
    else {
      this.loadPromise = firstValueFrom(forkJoin([
        this.getData(this.lastReportId, 1).pipe(first()).pipe(catchError(() => of(null))),
        this.getData(this.lastReportId, 2).pipe(first()).pipe(catchError(() => of(null))),
        this.getData(this.lastReportId, 3).pipe(first()).pipe(catchError(() => of(null))),
        this.getData(this.lastReportId, 4).pipe(first()).pipe(catchError(() => of(null)))
      ])).then((result) => {
        this.rvcData = {};
        this.rvcData[1] = result[0];
        this.rvcData[2] = result[1];
        this.rvcData[3] = result[2];
        this.rvcData[4] = result[3];
        return this.rvcData;
      });
      return this.loadPromise;
    }
  }

  /**
   * Get data
   * @param reportId
   * @param calculationId
   * @returns
   */
  private getData(reportId: number, calculationId: number) {
    return this.httpClient.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', 'reportwidgets/residualValueCalculation?reportId=' + reportId + '&calculationId=' + calculationId));
  }
  /**
   * Get mockup data
   * @returns
   */
  public getMockupData() {
    if (this.loadMockupPromise !== null) return this.loadMockupPromise;

    this.mockupData$ = this.httpClient.get<any>(this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', 'reportwidgets/residualValueCalculation/mockup?id=0'));

    this.loadMockupPromise = firstValueFrom(forkJoin([
      this.mockupData$
    ])).then((result) => {
      this.mockupData = {};
      //Deep copies! Otherwise, they would be the same reference
      this.mockupData[1] = ObjectUtil.deepCopy(result[0]);
      this.mockupData[2] = ObjectUtil.deepCopy(result[0]);
      this.mockupData[3] = ObjectUtil.deepCopy(result[0]);
      this.mockupData[4] = ObjectUtil.deepCopy(result[0]);

      if (this.mockupData == null) {
        alert("DEV: FATAL ERROR: Mockup data not loaded!");
      }
      return this.mockupData;
    });
    return this.loadMockupPromise;
  }
  /**
   * Round
   * @param valueUnrounded
   * @returns
   */
  private round(valueUnrounded) {
    const rounding = 1;

    return Math.round(this.safeDivide(valueUnrounded, rounding, null)) * rounding;
  }

  /**
   * Performs all necessary calculations. Re-calculates the entire model
   */
  public calculate(vm, changedFieldName) {
    //calc Calculation of the potential exit value fields

    try {
      //1. Calculation of the Potential Exit Value

      this.calculationOfPotentialExitValue(vm);

      //2. Calculation of Development Costs
      this.calculationOfDevelopmentCosts(vm, changedFieldName);

      //3. Total Net Exit Value after Completion
      this.calculateTotalNetExitValueAfterCompletion(vm);
    } catch (e) {
      alert("Exception occurred in residual widget calculations: " + JSON.stringify(e));
    }
    return vm.widgetData;
  }

  /**
   * Calculate the total Net Exit Value after completion
   * @param vm
   */
  private calculateTotalNetExitValueAfterCompletion(vm: any): void {
    vm.widgetData.TotalNetExitValueAfterCompletitionCalc = parseFloat(vm.widgetData.TotalNetExitValueCalc);

    //Total Development Costs
    vm.widgetData.ValuationResultsTotalDevelopmentCostsCalc = parseFloat(vm.widgetData.TotalDevelopmentCostsCalc);

    //Residuum
    vm.widgetData.ResiduumCalc = parseFloat(vm.widgetData.TotalNetExitValueAfterCompletitionCalc) - parseFloat(vm.widgetData.ValuationResultsTotalDevelopmentCostsCalc);

    //Financing costs (site)
    if (vm.widgetData.ResiduumCalc < 0) {
      vm.widgetData.FinancingCostsSiteCalc = 0;
    }
    else {
      vm.widgetData.FinancingCostsSiteCalc = parseFloat(vm.widgetData.ResiduumCalc) - this.safeDivide(parseFloat(vm.widgetData.ResiduumCalc), Math.pow((1 +
        parseFloat(vm.widgetData.FinancingCostsSitePercent) / 100), (parseFloat(vm.widgetData.FinancingCostsSiteMonths) / 12)), 0);
    }

    //Residuum after Financing
    vm.widgetData.ResiduumAfterFinancingCalc = vm.widgetData.ResiduumCalc - vm.widgetData.FinancingCostsSiteCalc;

    //Discounted Remaining Rental Income: Values

    vm.widgetData.DiscountedRemainingRentalIncome.forEach((entry => {
      entry.Total = this.safeDivide(parseFloat(entry.MonthlyRent) * parseFloat(entry.Months), Math.pow(1 + ((parseFloat(entry.DiscountRate) / 100)), (parseFloat(entry.Months) / 12)), 0);
    }));

    //Discounted Remaining Rental Income
    vm.widgetData.TotalDiscountedRemainingRentalIncome = vm.widgetData.DiscountedRemainingRentalIncome.reduce((a, b) => a + (parseFloat(b.Total) || 0), 0);

    //Purchaser's costs
    if (vm.widgetData.ResiduumAfterFinancingCalc + parseFloat(vm.widgetData.TotalDiscountedRemainingRentalIncome) < 0) {
      vm.widgetData.ValuationResultsPurchasersCostsCalc = 0;
    }
    else {
      vm.widgetData.ValuationResultsPurchasersCostsCalc = -this.safeDivide((parseFloat(vm.widgetData.ResiduumAfterFinancingCalc) + parseFloat(vm.widgetData.TotalDiscountedRemainingRentalIncome)), (1 + parseFloat(vm.widgetData.ValuationResultsPurchasersCosts) / 100), 0) + (parseFloat(vm.widgetData.ResiduumAfterFinancingCalc) + parseFloat(vm.widgetData.TotalDiscountedRemainingRentalIncome));
    }

    //Net Residual Value (unrounded)
    vm.widgetData.NetResidualValueUnroundedCalc = parseFloat(vm.widgetData.ResiduumAfterFinancingCalc) - parseFloat(vm.widgetData.TotalDiscountedRemainingRentalIncome) - parseFloat(vm.widgetData.ValuationResultsPurchasersCostsCalc);

    //Discounted Net Residual Value (unrounded)
    vm.widgetData.DiscountedNetResidualValueUnrounded = (parseFloat(vm.widgetData.NetResidualValueUnroundedCalc) * (this.safeDivide(1, Math.pow((1 + parseFloat(vm.widgetData.DiscountPeriodPercent) / 100), parseFloat(vm.widgetData.DiscountPeriodYears)), 0)))

    //Discounted Net Residual Value incl. Remaining Rental Income (unrounded)
    vm.widgetData.DiscountedNetResidualValueRemainingRentalIncomeUnrounded = parseFloat(vm.widgetData.TotalDiscountedRemainingRentalIncome) + parseFloat(vm.widgetData.DiscountedNetResidualValueUnrounded);

    //Other Surcharges / Deductions
    vm.widgetData.OtherSurchargesDeductionsTotal = vm.widgetData.OtherSurchargesDeductions.reduce((a, b) => a + (parseFloat(b.Value) || 0), 0);

    //Residual Value of Site (rounded)
    vm.widgetData.ResidualValueOfSiteRoundedDiscountedCalc = this.round(parseFloat(vm.widgetData.DiscountedNetResidualValueRemainingRentalIncomeUnrounded) + parseFloat(vm.widgetData.OtherSurchargesDeductionsTotal));

    //vm.widgetData.ResidualValueOfSiteRoundedGbaDiscountedCalc = this.safeDivide(vm.widgetData.ResidualValueOfSiteRoundedDiscountedCalc, grossBuildingArea, 0);
    //Bug 13230: Residual Widget - Calculation of Development Costs Results per sqm don't calculate correctly
    //Formula: =+P95/M47 => ResidualValueOfSiteRoundedDiscountedCalc / (Contruction Total Area 400)
    // we will assume that 300 and 400 should have same total BGFm² if CG300400Separately is used.. for CG300400Together we will take total we have
    const gbaDevideByValue = vm.widgetData.WidgetLayout === 'CG300400Separately' ? parseFloat(vm.widgetData.TotalGBACalcConstructionInstallations400) : parseFloat(vm.widgetData.TotalGBACalcConstructionInstallations);
    vm.widgetData.ResidualValueOfSiteRoundedGbaDiscountedCalc = this.safeDivide(parseFloat(vm.widgetData.ResidualValueOfSiteRoundedDiscountedCalc), gbaDevideByValue, 0);

    vm.widgetData.ResidualValueOfSiteRoundedSiteAreaDiscountedCalc = this.safeDivide(parseFloat(vm.widgetData.ResidualValueOfSiteRoundedDiscountedCalc), parseFloat(vm.widgetData.SiteArea), 0);
  }

  /**
   * Calculate the development costs
   * @param vm
   * @param changedFieldName
   */
  private calculationOfDevelopmentCosts(vm: any, changedFieldName: any): void {

    //return zero if undefined, null, NaN, etc..
    const zeroIfNoValue = (value) => value || 0;

    //2.1 200
    vm.widgetData.TotalArrangingCalc = vm.widgetData.ArrangingPreparationSiteServices.reduce((a, b) => a + zeroIfNoValue(b.Costs), 0);

    //2.2 300 + 400
    //Total Construction Costs (300&400 together)
    vm.widgetData.TotalConstructionCostsCalcTogether = vm.widgetData.ConstructionInstallations.reduce((a, b) => a + zeroIfNoValue(parseFloat(b.CostsTotal)), 0);
    vm.widgetData.TotalConstructionCostsCalcSeparately = vm.widgetData.ConstructionInstallations300.reduce((a, b) => a + zeroIfNoValue(parseFloat(b.CostsTotal)), 0)
      + vm.widgetData.ConstructionInstallations400.reduce((a, b) => a + zeroIfNoValue(parseFloat(b.CostsTotal)), 0);

    //Sums with VAT Damage checked
    const entriesWithVATDamageTogether = vm.widgetData.ConstructionInstallations.reduce((a, b) => a + (b.VatDamage ? zeroIfNoValue(parseFloat(b.CostsTotal)) : 0), 0);
    const entriesWithVATDamageSeparately = vm.widgetData.ConstructionInstallations300.reduce((a, b) => a + (b.VatDamage ? zeroIfNoValue(parseFloat(b.CostsTotal)) : 0), 0)
      + vm.widgetData.ConstructionInstallations400.reduce((a, b) => a + (b.VatDamage ? zeroIfNoValue(parseFloat(b.CostsTotal)) : 0), 0);

    const grossBuildingAreaTogether = vm.widgetData.ConstructionInstallations.reduce((a, b) => a + zeroIfNoValue(parseFloat(b.GrossBuildingAreaM2)), 0);

    vm.widgetData.TotalGBACalcConstructionInstallations = parseFloat(grossBuildingAreaTogether);
    vm.widgetData.TotalGBACalcConstructionInstallations300 = vm.widgetData.ConstructionInstallations300.reduce((a, b) => a + zeroIfNoValue(parseFloat(b.GrossBuildingAreaM2)), 0);
    vm.widgetData.TotalGBACalcConstructionInstallations400 = vm.widgetData.ConstructionInstallations400.reduce((a, b) => a + zeroIfNoValue(parseFloat(b.GrossBuildingAreaM2)), 0);

    vm.widgetData.CostsTotalCalcConstructionInstallations = parseFloat(vm.widgetData.TotalConstructionCostsCalcTogether);
    vm.widgetData.CostsTotalCalcConstructionInstallations300 = vm.widgetData.ConstructionInstallations300.reduce((a, b) => a + zeroIfNoValue(parseFloat(b.CostsTotal)), 0);
    vm.widgetData.CostsTotalCalcConstructionInstallations400 = vm.widgetData.ConstructionInstallations400.reduce((a, b) => a + zeroIfNoValue(parseFloat(b.CostsTotal)), 0);

    let entriesWithVATDamage;
    if (vm.widgetData.WidgetLayout === 'CG300400Separately') {
      vm.widgetData.TotalConstructionCostsCalc = parseFloat(vm.widgetData.TotalConstructionCostsCalcSeparately);
      entriesWithVATDamage = entriesWithVATDamageSeparately;
    }
    else {
      vm.widgetData.TotalConstructionCostsCalc = vm.widgetData.TotalConstructionCostsCalcTogether;
      entriesWithVATDamage = entriesWithVATDamageTogether;
    }

    //Contingencies
    vm.widgetData.ContingenciesCalc = parseFloat(vm.widgetData.Contingencies) / 100.0 * parseFloat(vm.widgetData.TotalConstructionCostsCalc);

    //Total Construction Costs (incl. Contingencies)
    vm.widgetData.TotalConstructionCostsInclContingenciesCalc = parseFloat(vm.widgetData.TotalConstructionCostsCalc) + parseFloat(vm.widgetData.ContingenciesCalc);

    //2.3 500 + 600
    //Outdoor facilities: Percentage
    //Calculated directly in the widget because both are editable

    //Total Outdoor Installations / Fittings & Artwork
    vm.widgetData.TotalOutdoorAndFittingCalc = parseFloat(vm.widgetData.OutdoorFacilitiesValue) + parseFloat(vm.widgetData.FittingsAndArtwork);

    //2.4 700 + 800

    //Professional fees
    vm.widgetData.ProfessionalFeesCalc = parseFloat(vm.widgetData.ProfessionalFees) / 100.0 * parseFloat(vm.widgetData.TotalConstructionCostsCalc);

    //Section from Additional Development Costs, which is required for further calculations

    //Incentives
    vm.widgetData.IncentivesCalc = parseFloat(vm.widgetData.Incentives) / 12 * parseFloat(vm.widgetData.InvScMarketRentCalc);

    //Agent fees
    vm.widgetData.AgentFeesCalc = parseFloat(vm.widgetData.AgentFees) * parseFloat(vm.widgetData.InvScMarketRentCalc) / 12;

    //Vacancy after completion
    vm.widgetData.VacancyAfterCompletionCalc = parseFloat(vm.widgetData.InvScMarketRentCalc) / 12 * parseFloat(vm.widgetData.VacancyAfterCompletionMonths) * parseFloat(vm.widgetData.VacancyAfterCompletionPercent) / 100.0;

    //VAT damage
    vm.widgetData.VatDamageCalc = (entriesWithVATDamage + this.safeDivide(entriesWithVATDamage, parseFloat(vm.widgetData.TotalConstructionCostsCalc), 0)
      * (
        parseFloat(vm.widgetData.ContingenciesCalc)
        + parseFloat(vm.widgetData.TotalArrangingCalc)
        + parseFloat(vm.widgetData.ProfessionalFeesCalc)
        + parseFloat(vm.widgetData.OtherAncillaryCosts)
        + parseFloat(vm.widgetData.TotalOutdoorAndFittingCalc)
      )) * parseFloat(vm.widgetData.VatDamage) / 100.0;

    //determine which one is manual field: 1 = DeveloperProfitPercent, 2 = DeveloperProfitPercentOfTotalCalc
    this.calculateDeveloperProfit(changedFieldName, vm);

    //Interest during construction
    const interestsCostsSum = parseFloat(vm.widgetData.TotalConstructionCostsInclContingenciesCalc)
      + parseFloat(vm.widgetData.TotalArrangingCalc)
      + parseFloat(vm.widgetData.TotalOutdoorAndFittingCalc)
      + parseFloat(vm.widgetData.IncentivesCalc)
      + parseFloat(vm.widgetData.AgentFeesCalc)
      + parseFloat(vm.widgetData.VacancyAfterCompletionCalc)
      + parseFloat(vm.widgetData.VatDamageCalc)
      + parseFloat(vm.widgetData.ProfessionalFeesCalc)
      + parseFloat(vm.widgetData.OtherAncillaryCosts)
      - Math.abs(parseFloat(vm.widgetData.AlreadyInvestedCosts));

    vm.widgetData.InterestDuringConstructionCalc = ((interestsCostsSum) * Math.pow((1 + parseFloat(vm.widgetData.InterestDuringConstructionPercentage) / 100.0), ((parseFloat(vm.widgetData.InterestDuringConstructionMonths) / 12) / 2))) - (interestsCostsSum)

    //Total Ancillary Construction and Financing Costs
    vm.widgetData.TotalAncillaryCostsCalc = parseFloat(vm.widgetData.ProfessionalFeesCalc)
      + parseFloat(vm.widgetData.OtherAncillaryCosts)
      + parseFloat(vm.widgetData.InterestDuringConstructionCalc)
      + parseFloat(vm.widgetData.OtherFinancingCosts);

    vm.widgetData.TotalAdditionalDevCostsCalc = parseFloat(vm.widgetData.IncentivesCalc)
      + parseFloat(vm.widgetData.AgentFeesCalc)
      + parseFloat(vm.widgetData.Marketing)
      + parseFloat(vm.widgetData.VacancyAfterCompletionCalc)
      + parseFloat(vm.widgetData.VatDamageCalc)
      + parseFloat(vm.widgetData.DeveloperProfitPercentCalc);

    //2.5 Additional Development Costs. Must be calculated before! 700 + 800
    //-> Other calculations: See above!

    if (vm.widgetData.DeveloperProfitPercentManualField !== 2) {
      //if not manual field (if DeveloperProfitPercent is manual field), calc this one
      vm.widgetData.DeveloperProfitPercentOfTotalCalc = (parseFloat(vm.widgetData.DeveloperProfitPercentCalc) || 0) / (
        parseFloat(vm.widgetData.IncentivesCalc)
        + parseFloat(vm.widgetData.AgentFeesCalc)
        + parseFloat(vm.widgetData.VacancyAfterCompletionCalc)
        + parseFloat(vm.widgetData.VatDamageCalc)
        + parseFloat(vm.widgetData.TotalArrangingCalc)
        + parseFloat(vm.widgetData.TotalConstructionCostsInclContingenciesCalc)
        + parseFloat(vm.widgetData.TotalOutdoorAndFittingCalc)
        + parseFloat(vm.widgetData.TotalAncillaryCostsCalc)
      ) * 100.0;
    }

    //Total Development Costs
    const TotalDevelopmentCostsCalcUnrounded = parseFloat(vm.widgetData.TotalArrangingCalc)
      + parseFloat(vm.widgetData.TotalConstructionCostsInclContingenciesCalc)
      + parseFloat(vm.widgetData.TotalOutdoorAndFittingCalc)
      + parseFloat(vm.widgetData.TotalAncillaryCostsCalc)
      + parseFloat(vm.widgetData.TotalAdditionalDevCostsCalc)
      - Math.abs(parseFloat(vm.widgetData.AlreadyInvestedCosts));

    vm.widgetData.TotalDevelopmentCostsCalc = this.round(TotalDevelopmentCostsCalcUnrounded);

  }

  /**
   * Calculate the developer profit
   * @param changedFieldName
   * @param vm
   */
  private calculateDeveloperProfit(changedFieldName: any, vm: any) {
    this.determineDeveloperProfitManualField(changedFieldName, vm);

    if (vm.widgetData.DeveloperProfitPercentManualField !== 1) {
      //if not manual field (if DeveloperProfitPercentOfTotalCalc is manual field), calc this one
      vm.widgetData.DeveloperProfitPercentCalc =
        ((parseFloat(vm.widgetData.DeveloperProfitPercentOfTotalCalc) || 0) / 100)
        * (
          parseFloat(vm.widgetData.IncentivesCalc)
          + parseFloat(vm.widgetData.AgentFeesCalc)
          + parseFloat(vm.widgetData.VacancyAfterCompletionCalc)
          + parseFloat(vm.widgetData.VatDamageCalc)
          + parseFloat(vm.widgetData.TotalArrangingCalc)
          + parseFloat(vm.widgetData.TotalConstructionCostsInclContingenciesCalc)
          + parseFloat(vm.widgetData.TotalOutdoorAndFittingCalc)
          + parseFloat(vm.widgetData.TotalAncillaryCostsCalc)
        );

      vm.widgetData.DeveloperProfitPercent = this.safeDivide(parseFloat(vm.widgetData.DeveloperProfitPercentCalc), parseFloat(vm.widgetData.TotalNetExitValueCalc), 0) * 100.0;
    } else {
      //Developer profit in % of Net Exit Value after Completion
      vm.widgetData.DeveloperProfitPercentCalc = (parseFloat(vm.widgetData.DeveloperProfitPercent) / 100.0) * parseFloat(vm.widgetData.TotalNetExitValueCalc);
    }
  }

  /**
   * Determine the manual override field for the developer profit
   * @param changedFieldName
   * @param vm
   */
  private determineDeveloperProfitManualField(changedFieldName: any, vm: any) {
    switch (changedFieldName) {
      case 'DeveloperProfitPercent':
        vm.widgetData.DeveloperProfitPercentManualField = 1;
        break;
      case 'DeveloperProfitPercentOfTotalCalc':
        vm.widgetData.DeveloperProfitPercentManualField = 2;
        break;
      default:
        //manual field stayed the same
        break;
    }
  }

  /**
   * Calculate the potential exit value
   * @param vm
   */
  private calculationOfPotentialExitValue(vm: any): void {

    //return zero if undefined, null, NaN, etc..
    const zeroIfNoValue = (value) => value || 0;

    //1.1 Letting scenario
    //Calculations for the upper table
    this.calculateLettingScenariosMarketRentEuroPerYearCalc(vm);

    //Lettable area sum
    vm.widgetData.InvScLettableAreaSumCalc = vm.widgetData.LettingScenarios.reduce((a, b) => a + zeroIfNoValue(parseFloat(b.LettableArea)), 0);

    //Lettable units sum
    vm.widgetData.InvScLettableUnitsSumCalc = vm.widgetData.LettingScenarios.reduce((a, b) => a + zeroIfNoValue(parseFloat(b.UnitsQuantity)), 0);

    //Rent per year
    vm.widgetData.InvScMarketRentEuroPerYearSumCalc = vm.widgetData.LettingScenarios.reduce((a, b) => a + zeroIfNoValue(parseFloat(b.MarketRentEuroPerYearCalc)), 0);
    vm.widgetData.InvScMarketRentCalc = vm.widgetData.InvScMarketRentEuroPerYearSumCalc;

    //Rent per sqm per year
    vm.widgetData.InvScMarketRentEuroPerQmPerMonthSumCalc = this.safeDivide(vm.widgetData.InvScMarketRentEuroPerYearSumCalc, vm.widgetData.InvScLettableAreaSumCalc, 0.0) / 12.0;

    //Maintenance costs (lettable area)
    vm.widgetData.MaintenanceCostsLettableAreaCalc = parseFloat(vm.widgetData.MaintenanceCostsLettableArea) * parseFloat(vm.widgetData.InvScLettableAreaSumCalc);

    //Maintenance costs (parking)
    const parkingScenarios = vm.widgetData.LettingScenarios.filter(s => s.UseCategoryId == 19 || s.UseCategoryId == 20);
    vm.widgetData.MaintenanceCostsParkingCalc = parseFloat(vm.widgetData.MaintenanceCostsParking) * parkingScenarios.reduce((a, b) => a + zeroIfNoValue(parseFloat(b.UnitsQuantity)), 0);

    //Management costs (commercial use)
    //Commercial is everything but residential (and parking)
    const commercialScenarios = vm.widgetData.LettingScenarios.filter(s => s.UseCategoryId != 19 && s.UseCategoryId != 20 && s.UseCategoryId != 25 && s.UseCategoryId != 26);
    vm.widgetData.ManagementCostsCommercialCalc = (parseFloat(vm.widgetData.ManagementCostsCommercial) / 100.0) * commercialScenarios.reduce((a, b) => a + zeroIfNoValue(parseFloat(b.MarketRentEuroPerYearCalc)), 0);

    //Management costs (residential use)
    const residentialScenarios = vm.widgetData.LettingScenarios.filter(s => s.UseCategoryId == 25 || s.UseCategoryId == 26);
    vm.widgetData.ManagementCostsResidentialCalc = parseFloat(vm.widgetData.ManagementCostsResidential) * residentialScenarios.reduce((a, b) => a + zeroIfNoValue(parseFloat(b.UnitsQuantity)), 0);

    //Ground tax
    vm.widgetData.GroundTaxCalc = parseFloat(vm.widgetData.GroundTax);

    //Insurance costs
    vm.widgetData.InsuranceCostsCalc = parseFloat(vm.widgetData.InsuranceCosts);

    //Other costs
    vm.widgetData.OtherCostsCalc = parseFloat(vm.widgetData.OtherCosts) / 100.0 * parseFloat(vm.widgetData.InvScMarketRentCalc);

    //Further specific costs
    vm.widgetData.FurtherSpecificCostsCalc = parseFloat(vm.widgetData.FurtherSpecificCosts) / 100.0 * parseFloat(vm.widgetData.InvScMarketRentCalc);

    //Leasehold costs
    vm.widgetData.LeaseholdCostsCalc = parseFloat(vm.widgetData.LeaseholdCosts);

    //Net Operating Income
    vm.widgetData.NetOperatingIncomeCalc = parseFloat(vm.widgetData.InvScMarketRentCalc) - (
      parseFloat(vm.widgetData.MaintenanceCostsLettableAreaCalc)
      + parseFloat(vm.widgetData.MaintenanceCostsParkingCalc)
      + parseFloat(vm.widgetData.ManagementCostsCommercialCalc)
      + parseFloat(vm.widgetData.ManagementCostsResidentialCalc)
      + parseFloat(vm.widgetData.GroundTaxCalc)
      + parseFloat(vm.widgetData.InsuranceCostsCalc)
      + parseFloat(vm.widgetData.OtherCostsCalc)
      + parseFloat(vm.widgetData.FurtherSpecificCostsCalc)
      + parseFloat(vm.widgetData.LeaseholdCostsCalc)
    );

    //Gross Capital Value (rounded)
    vm.widgetData.GrossCapitalValueCalc = this.safeDivide(parseFloat(vm.widgetData.NetOperatingIncomeCalc), (parseFloat(vm.widgetData.CapitalisationRate) / 100.0), 0.0);

    //Purchaser's costs
    vm.widgetData.PurchasersCostsCalc = 0;

    //Net Capital Value Investment Scenario
    vm.widgetData.InvScNetCapitalValueCalc = this.safeDivide(parseFloat(vm.widgetData.GrossCapitalValueCalc), (1.0 + parseFloat(vm.widgetData.PurchasersCostsCalc) / 100.0), 0.0);

    //Net Capital Value Investment Scenario multiplier
    vm.widgetData.InvScNetCapitalValuePercentageCalc = this.safeDivide(parseFloat(vm.widgetData.InvScNetCapitalValueCalc), parseFloat(vm.widgetData.InvScMarketRentCalc), 0.0);

    //Net Capital Value Investment Scenario per sqm
    vm.widgetData.InvScNetCapitalValueEurPerQmCalc = this.safeDivide(parseFloat(vm.widgetData.InvScNetCapitalValueCalc), parseFloat(vm.widgetData.InvScLettableAreaSumCalc), 0.0);

    //1.2 Sales scenario
    //Calculations for the upper table
    this.calculateSalesPriceTotalCalc(vm);
    //Lettable units sum
    vm.widgetData.SoCLettableUnitsSumCalc = vm.widgetData.SalesScenarios.reduce((a, b) => a + zeroIfNoValue(parseFloat(b.UnitsQuantity)), 0);

    //Lettable Area sum
    vm.widgetData.SoCLettableAreaSumCalc = vm.widgetData.SalesScenarios.reduce((a, b) => a + zeroIfNoValue(parseFloat(b.LettableArea)), 0);

    //Sales Price total
    vm.widgetData.SoCSalesPriceTotalSumCalc = vm.widgetData.SalesScenarios.reduce((a, b) => a + zeroIfNoValue(parseFloat(b.SalesPriceTotalCalc)), 0);

    //Net Capital Value Sales Scenario per sqm
    vm.widgetData.SoCSalesPriceEuroQmTotalSumCalc = this.safeDivide(parseFloat(vm.widgetData.SoCSalesPriceTotalSumCalc), parseFloat(vm.widgetData.SoCLettableAreaSumCalc), null);

    //Total Net Exit Value after Completion
    if (vm.widgetData.WidgetLayout === 'LettingScenario') {
      vm.widgetData.TotalNetExitValueCalc = this.round(parseFloat(vm.widgetData.InvScNetCapitalValueCalc));
    } else if (vm.widgetData.WidgetLayout === 'SalesScenario') {
      vm.widgetData.TotalNetExitValueCalc = this.round(parseFloat(vm.widgetData.SoCSalesPriceTotalSumCalc));
    } else if (vm.widgetData.WidgetLayout === 'LettingAndSalesScenario') {
      vm.widgetData.TotalNetExitValueCalc = this.round(parseFloat(vm.widgetData.InvScNetCapitalValueCalc) + parseFloat(vm.widgetData.SoCSalesPriceTotalSumCalc));
    }
  }

  /**
   * Calculate the market rent for letting scenarios
   * @param vm
   */
  private calculateLettingScenariosMarketRentEuroPerYearCalc(vm: any) {
    vm.widgetData.LettingScenarios.forEach((entry => {
      if (entry.LettableArea) {
        entry.MarketRentEuroPerYearCalc = parseFloat(entry.LettableArea) * parseFloat(entry.MarketRentEuroPerQmPerMonth) * 12.0;
      }
      else {
        entry.MarketRentEuroPerYearCalc = parseFloat(entry.UnitsQuantity) * parseFloat(entry.MarketRentEuroPerQmPerMont) * 12.0;
      }
    }));
  }

  /**
   * Calculate the total sale price
   * @param vm
   */
  private calculateSalesPriceTotalCalc(vm: any) {
    vm.widgetData.SalesScenarios.forEach((entry => {
      if (entry.LettableArea) {
        entry.SalesPriceTotalCalc = parseFloat(entry.LettableArea) * parseFloat(entry.SalesPriceEuroQm);
      }
      else {
        entry.SalesPriceTotalCalc = parseFloat(entry.UnitsQuantity) * parseFloat(entry.SalesPriceEuroQm);
      }
    }));
  }

  /**
   * Performs a safe division of 2 values.
   * @param {any} numerator The numerator('above the line')
   * @param {any} denominator The denominator('below the line')
   * @param {any} defaultValue The default value if the denominator is 0
   */
  private safeDivide(numerator, denominator, defaultValue) {
    const fNumerator = parseFloat(numerator);
    const fDenominator = parseFloat(denominator);

    if (isNaN(fNumerator) || isNaN(fDenominator) || !fDenominator) {
      return defaultValue;
    }

    return fNumerator / fDenominator;
  }
}
